import React, { useState } from 'react'; // Import useState
import { Link, useNavigate } from 'react-router-dom'; // Import useNavigate
import { auth, db } from '../firebase'; // Import auth and db instances
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
  EmailAuthProvider,
  linkWithCredential
} from 'firebase/auth';
import { collection, query, where, getDocs, doc, setDoc, getDoc } from 'firebase/firestore'; // Import Firestore functions

function HomePage() {
    const navigate = useNavigate(); // Initialize navigate
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(''); // State for login errors
    const [loading, setLoading] = useState(false); // State for loading indicator
    const [isSignUp, setIsSignUp] = useState(false); // Toggle between login and signup
    const [passwordStrength, setPasswordStrength] = useState('');
    const [showPasswordSetup, setShowPasswordSetup] = useState(false); // State for password setup modal
    const [gmailUser, setGmailUser] = useState(null); // Store Gmail authenticated user
    const [showResendVerification, setShowResendVerification] = useState(false); // State for resend verification
    const [lastCreatedUser, setLastCreatedUser] = useState(null); // Store last created user for resend

    // Password validation function
    const validatePassword = (password) => {
      const minLength = 8;
      const hasUpperCase = /[A-Z]/.test(password);
      const hasLowerCase = /[a-z]/.test(password);
      const hasNumbers = /\d/.test(password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

      if (password.length < minLength) {
        return { isValid: false, message: 'Password must be at least 8 characters long', strength: 'weak' };
      }
      
      let score = 0;
      if (hasUpperCase) score++;
      if (hasLowerCase) score++;
      if (hasNumbers) score++;
      if (hasSpecialChar) score++;

      if (score < 3) {
        return { isValid: false, message: 'Password must contain uppercase, lowercase, and numbers', strength: 'weak' };
      }
      
      const strength = score === 4 ? 'strong' : score === 3 ? 'medium' : 'weak';
      return { isValid: true, message: 'Password is strong', strength };
    };

    // Handle password change with validation
    const handlePasswordChange = (newPassword) => {
      setPassword(newPassword);
      if (newPassword) {
        const validation = validatePassword(newPassword);
        setPasswordStrength(validation.strength);
      } else {
        setPasswordStrength('');
      }
    };

    // Gmail authentication handler
    const handleGmailAuth = async () => {
      setError('');
      setLoading(true);
      
      try {
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);
        const user = result.user;
        
        console.log('Gmail authentication successful:', user.email);
        
        // Check if user already has a restaurant
        const restaurantsRef = collection(db, 'restaurants');
        const q = query(restaurantsRef, where('email', '==', user.email));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          // User has existing restaurant, navigate to dashboard
          const restaurantDoc = querySnapshot.docs[0];
          console.log('Existing restaurant found:', restaurantDoc.data().name);
          navigate(`/dashboard/${restaurantDoc.id}`);
        } else {
          // New user, show password setup modal
          console.log('New Gmail user, showing password setup');
          setGmailUser(user);
          setShowPasswordSetup(true);
        }
        
      } catch (error) {
        console.error('Gmail authentication failed:', error);
        setError(`Gmail authentication failed: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Password setup handler for Gmail users
    const handlePasswordSetup = async () => {
      setError('');
      
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.message);
        return;
      }
      
      setLoading(true);
      
      try {
        // Link password credential to the existing Gmail user
        const credential = EmailAuthProvider.credential(gmailUser.email, password);
        await linkWithCredential(gmailUser, credential);
        
        console.log('Password linked to Gmail account successfully');
        
        // Close password setup modal and navigate to create restaurant
        setShowPasswordSetup(false);
        setGmailUser(null);
        setPassword('');
        setConfirmPassword('');
        navigate('/create');
        
      } catch (error) {
        console.error('Password setup failed:', error);
        setError(`Password setup failed: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Resend verification email handler
    const handleResendVerification = async () => {
      if (!lastCreatedUser) {
        setError('No user found to resend verification email.');
        return;
      }

      setError('');
      setLoading(true);

      try {
        await sendEmailVerification(lastCreatedUser, {
          url: window.location.origin + '/create',
          handleCodeInApp: false
        });
        console.log('Verification email resent to:', lastCreatedUser.email);
        alert('Verification email has been resent! Please check your email (including spam folder).');
      } catch (error) {
        console.error('Failed to resend verification email:', error);
        setError(`Failed to resend verification email: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    // Email/Password signup handler
    const handleSignUp = async () => {
      setError('');
      
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
      
      const passwordValidation = validatePassword(password);
      if (!passwordValidation.isValid) {
        setError(passwordValidation.message);
        return;
      }
      
      setLoading(true);
      
      try {
        // Create user with email and password
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;
        
        // Send email verification
        try {
          await sendEmailVerification(user, {
            url: window.location.origin + '/create', // Redirect URL after verification
            handleCodeInApp: false
          });
          console.log('Email verification sent successfully to:', user.email);
        } catch (verificationError) {
          console.error('Failed to send verification email:', verificationError);
          // Don't block the signup process if email sending fails
        }
        
        console.log('User created successfully:', user.email);
        setLastCreatedUser(user); // Store user for potential resend
        setShowResendVerification(true); // Show resend option
        alert('Account created successfully! Please check your email (including spam folder) for verification before creating your restaurant.');
        
        // Navigate to create restaurant page
        navigate('/create');
        
      } catch (error) {
        console.error('Sign up failed:', error);
        let errorMessage = 'Sign up failed: ';
        
        switch (error.code) {
          case 'auth/email-already-in-use':
            errorMessage += 'This email is already registered. Please use the login form instead.';
            break;
          case 'auth/invalid-email':
            errorMessage += 'Please enter a valid email address.';
            break;
          case 'auth/weak-password':
            errorMessage += 'Password is too weak. Please choose a stronger password.';
            break;
          default:
            errorMessage += error.message;
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    // Login handler for backward compatibility with existing users
    const handleLogin = async () => {
      setError(''); // Clear previous errors
      setLoading(true);
      try {
        console.log('Attempting login for:', email);
        
        // First try Firebase Auth login for new users
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;
          
          console.log('Firebase Auth login successful:', user.email);
          
          // Check if user's email is verified (skip for Gmail-linked accounts)
          if (!user.emailVerified) {
            // Check if this user has Gmail as a provider (which means they're already verified)
            const hasGoogleProvider = user.providerData.some(provider => provider.providerId === 'google.com');
            
            if (!hasGoogleProvider) {
              setError('Please verify your email before logging in. Check your inbox for the verification link.');
              setLoading(false);
              return;
            }
          }
          
          // Check if user has a restaurant
          const restaurantsRef = collection(db, 'restaurants');
          const q = query(restaurantsRef, where('email', '==', user.email));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const restaurantDoc = querySnapshot.docs[0];
            console.log('Restaurant found:', restaurantDoc.data().name);
            navigate(`/dashboard/${restaurantDoc.id}`);
          } else {
            // New authenticated user, redirect to create restaurant
            navigate('/create');
          }
          
        } catch (authError) {
          console.log('Firebase Auth failed, trying legacy method:', authError.code);
          
          // Fallback to legacy Firestore-only authentication for existing users
          if (authError.code === 'auth/user-not-found' || authError.code === 'auth/wrong-password' || authError.code === 'auth/invalid-credential') {
            console.log('Attempting legacy Firestore login check for:', email);
            
            // Query the 'restaurants' collection for the provided email
            const restaurantsRef = collection(db, 'restaurants');
            const q = query(restaurantsRef, where('email', '==', email));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
              // No restaurant found with this email
              console.log('No restaurant found with email:', email);
              setError('Invalid email or password.');
            } else if (querySnapshot.size === 1) {
              // Exactly one restaurant found
              const restaurantDoc = querySnapshot.docs[0];
              const restaurantData = restaurantDoc.data();
              console.log('Restaurant found (legacy):', restaurantData.name);

              // Compare the provided password with the stored password
              if (password === restaurantData.password) {
                // Passwords match - legacy user
                console.log('Legacy password matches for:', email);
                // Navigate to the dashboard page using the restaurant's document ID
                navigate(`/dashboard/${restaurantDoc.id}`); 
              } else {
                // Passwords do not match
                console.log('Password mismatch for:', email);
                setError('Invalid email or password.');
              }
            } else {
              // Multiple restaurants found with the same email (should not happen ideally)
              console.warn('Multiple restaurants found with email:', email);
              setError('Login error: Multiple accounts found. Please contact support.');
            }
          } else {
            // Other Firebase Auth errors
            throw authError;
          }
        }

      } catch (error) {
        console.error('Login check failed:', error);
        let errorMessage = 'Login failed: ';
        
        switch (error.code) {
          case 'auth/invalid-email':
            errorMessage += 'Please enter a valid email address.';
            break;
          case 'auth/user-disabled':
            errorMessage += 'This account has been disabled.';
            break;
          case 'auth/too-many-requests':
            errorMessage += 'Too many failed attempts. Please try again later.';
            break;
          default:
            errorMessage += error.message;
        }
        
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    return (
      <div className="container py-8" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header Section */}
        <div className="header" style={{
          textAlign: 'center',
          width: '100%',
          margin: '0 auto',
          marginBottom: '3rem',
          backgroundColor: '#dc2626'
        }}>
          <h1 style={{
            textAlign: 'center',
            margin: '0 auto',
            fontSize: '3rem',
            fontWeight: 'bold',
            color: '#fff',
            letterSpacing: '-0.025em'
          }}>CheckMenus</h1>
        </div>

        <div className="centered px-4" style={{ maxWidth: '800px', margin: '0 auto' }}>
          {/* Welcome Section */}
          <h2
            className="text-2xl"
            style={{
              marginBottom: '1.5rem',
              fontSize: '1.75rem',
              fontWeight: '600',
              color: '#4a5568',
              textAlign: 'center'
            }}
          >
            Welcome to CheckMenus
          </h2>

          <p
            className="text-gray-600 max-w-2xl mx-auto"
            style={{
              lineHeight: '1.8',
              marginBottom: '2.5rem',
              fontSize: '1.1rem',
              color: '#718096',
              textAlign: 'center'
            }}
          >
            Create and manage your restaurant's menu with ease. Share your menu online with a unique URL.
          </p>

          {/* <div
            className="form-group"
            style={{
              marginBottom: '3.5rem',
              textAlign: 'center'
            }}
          >
            <Link
              to="/create"
              className="btn py-3 px-6"
              style={{
                textDecoration: 'none',
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#dc2626',
                color: 'white',
                fontWeight: '600',
                borderRadius: '0.375rem',
                boxShadow: '0 4px 6px rgba(52, 26, 26, 0.25)',
                transition: 'all 0.2s ease',
                fontSize: '1.1rem'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#8f0303'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
            >
              Create New Restaurant Menu
            </Link>
          </div> */}

          {/* Login Section with Enhanced UI */}
          <div
            className="login-section"
            style={{
              marginTop: '2rem',
              paddingTop: '2.5rem',
              paddingBottom: '2.5rem',
              borderTop: '1px solid rgb(237, 237, 237)',
              borderRadius: '0.7rem',
              maxWidth: '450px',
              margin: '0 auto',
              backgroundColor: '#f8fafc',
              boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
              padding: '2rem'
            }}
          >
            <h3
              style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#4a5568',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}
            >
              Restaurant Owner Access
            </h3>
            
            {/* Gmail Authentication Button */}
            <button
              onClick={handleGmailAuth}
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                backgroundColor: '#4285f4',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 4px rgba(66, 133, 244, 0.3)',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#3367d6'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#4285f4'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                <path fill="white" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="white" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="white" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="white" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? 'Signing in...' : 'Continue with Gmail'}
            </button>

            <div style={{ 
              textAlign: 'center', 
              margin: '1rem 0', 
              color: '#718096',
              fontSize: '0.875rem',
              position: 'relative'
            }}>
              <span style={{ 
                backgroundColor: '#f8fafc', 
                padding: '0 1rem',
                position: 'relative',
                zIndex: 1
              }}>
                or
              </span>
              <div style={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: '1px',
                backgroundColor: '#e2e8f0',
                zIndex: 0
              }}></div>
            </div>

            {/* Toggle between Login and Sign Up */}
            <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
              <button
                onClick={() => setIsSignUp(!isSignUp)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#4299e1',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
              >
                {isSignUp ? 'Already have an account? Login' : 'New user? Create account'}
              </button>
              
              {/* Resend Verification Email Button */}
              {showResendVerification && isSignUp && (
                <button
                  onClick={handleResendVerification}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    backgroundColor: 'transparent',
                    color: '#4299e1',
                    border: '1px solid #4299e1',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    marginTop: '0.5rem'
                  }}
                  onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#4299e1', e.target.style.color = 'white')}
                  onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = 'transparent', e.target.style.color = '#4299e1')}
                >
                  {loading ? 'Sending...' : 'Resend Verification Email'}
                </button>
              )}
            </div>
            
            {error && (
              <div 
                className="error-message" 
                style={{
                  backgroundColor: '#FEE2E2',
                  color: '#B91C1C',
                  padding: '0.75rem',
                  borderRadius: '0.375rem',
                  marginBottom: '1.5rem',
                  fontSize: '0.875rem',
                  textAlign: 'center'
                }}
              >
                {error}
              </div>
            )}
            
            <div className="login-form" style={{ width: '100%' }}>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <label 
                  htmlFor="email" 
                  style={{ 
                    display: 'block', 
                    marginBottom: '0.5rem', 
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#4a5568',
                    textAlign: 'left'
                  }}
                >
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4299e1'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: isSignUp ? '1rem' : '1.5rem' }}>
                <label 
                  htmlFor="password" 
                  style={{ 
                    display: 'block', 
                    marginBottom: '0.5rem', 
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#4a5568',
                    textAlign: 'left'
                  }}
                >
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => isSignUp ? handlePasswordChange(e.target.value) : setPassword(e.target.value)}
                  placeholder={isSignUp ? "Choose a strong password" : "Enter your password"}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4299e1'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  onKeyPress={(e) => e.key === 'Enter' && (isSignUp ? handleSignUp() : handleLogin())}
                />
                
                {/* Password Strength Indicator */}
                {isSignUp && password && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      color: passwordStrength === 'strong' ? '#10b981' : passwordStrength === 'medium' ? '#f59e0b' : '#ef4444',
                      marginBottom: '0.25rem'
                    }}>
                      Password strength: {passwordStrength}
                    </div>
                    <div style={{ 
                      width: '100%', 
                      height: '4px', 
                      backgroundColor: '#e5e7eb', 
                      borderRadius: '2px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: passwordStrength === 'strong' ? '100%' : passwordStrength === 'medium' ? '66%' : '33%',
                        height: '100%',
                        backgroundColor: passwordStrength === 'strong' ? '#10b981' : passwordStrength === 'medium' ? '#f59e0b' : '#ef4444',
                        transition: 'all 0.3s ease'
                      }}></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Confirm Password for Sign Up */}
              {isSignUp && (
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                  <label 
                    htmlFor="confirmPassword" 
                    style={{ 
                      display: 'block', 
                      marginBottom: '0.5rem', 
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#4a5568',
                      textAlign: 'left'
                    }}
                  >
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      border: '1px solid #e2e8f0',
                      borderRadius: '0.375rem',
                      fontSize: '1rem',
                      transition: 'border-color 0.2s ease',
                      outline: 'none'
                    }}
                    onFocus={(e) => e.target.style.borderColor = '#4299e1'}
                    onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                    onKeyPress={(e) => e.key === 'Enter' && handleSignUp()}
                  />
                </div>
              )}
              
              <button
                onClick={isSignUp ? handleSignUp : handleLogin}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(28, 26, 26, 0.1)'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#575656'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#000'}
                disabled={loading}
              >
                {loading ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ 
                      display: 'inline-block', 
                      width: '1rem', 
                      height: '1rem', 
                      borderRadius: '50%', 
                      border: '2px solid rgba(255,255,255,0.3)', 
                      borderTopColor: 'white',
                      animation: 'spin 1s linear infinite',
                      marginRight: '0.5rem'
                    }}></span>
                    {isSignUp ? 'Creating Account...' : 'Logging in...'}
                  </span>
                ) : (isSignUp ? 'Create Account' : 'Login')}
              </button>
            </div>
          </div>
          {/* End Login Section */}

        </div>
        
        {/* Password Setup Modal for Gmail Users */}
        {showPasswordSetup && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              padding: '2rem',
              width: '90%',
              maxWidth: '400px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
            }}>
              <h2 style={{
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '1rem',
                textAlign: 'center',
                color: '#2d3748'
              }}>
                Set Up Password
              </h2>
              
              <p style={{
                fontSize: '0.875rem',
                color: '#718096',
                marginBottom: '1.5rem',
                textAlign: 'center'
              }}>
                Create a password to enable both Gmail and password login for your account ({gmailUser?.email})
              </p>
              
              {error && (
                <div style={{
                  backgroundColor: '#fed7d7',
                  color: '#c53030',
                  padding: '0.75rem',
                  borderRadius: '0.375rem',
                  marginBottom: '1rem',
                  fontSize: '0.875rem',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}
              
              <div style={{ marginBottom: '1rem' }}>
                <label 
                  htmlFor="modalPassword"
                  style={{
                    display: 'block', 
                    marginBottom: '0.5rem', 
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#4a5568',
                    textAlign: 'left'
                  }}
                >
                  Password
                </label>
                <input
                  id="modalPassword"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4299e1'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                {password && (
                  <div style={{
                    fontSize: '0.75rem',
                    marginTop: '0.25rem',
                    color: passwordStrength === 'strong' ? '#38a169' : 
                           passwordStrength === 'medium' ? '#d69e2e' : '#e53e3e'
                  }}>
                    Password strength: {passwordStrength}
                  </div>
                )}
              </div>
              
              <div style={{ marginBottom: '1.5rem' }}>
                <label 
                  htmlFor="modalConfirmPassword"
                  style={{
                    display: 'block', 
                    marginBottom: '0.5rem', 
                    fontSize: '0.875rem',
                    fontWeight: '500',
                    color: '#4a5568',
                    textAlign: 'left'
                  }}
                >
                  Confirm Password
                </label>
                <input
                  id="modalConfirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    transition: 'border-color 0.2s ease',
                    outline: 'none'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#4299e1'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                  onKeyPress={(e) => e.key === 'Enter' && handlePasswordSetup()}
                />
              </div>
              
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button
                  onClick={() => {
                    setShowPasswordSetup(false);
                    setGmailUser(null);
                    setPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1rem',
                    backgroundColor: '#e2e8f0',
                    color: '#4a5568',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#cbd5e0'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#e2e8f0'}
                >
                  Cancel
                </button>
                
                <button
                  onClick={handlePasswordSetup}
                  disabled={loading}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1rem',
                    backgroundColor: loading ? '#a0aec0' : '#000',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.375rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => !loading && (e.target.style.backgroundColor = '#2d3748')}
                  onMouseLeave={(e) => !loading && (e.target.style.backgroundColor = '#000')}
                >
                  {loading ? (
                    <>
                      <div style={{
                        width: '1rem',
                        height: '1rem',
                        border: '2px solid transparent',
                        borderTop: '2px solid white',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginRight: '0.5rem'
                      }}></div>
                      Setting up...
                    </>
                  ) : (
                    'Set Password'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Add a style tag for animations */}
        <style jsx>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

export default HomePage;